import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, CircularProgress, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CalculateIcon from '@mui/icons-material/Calculate';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ShieldIcon from '@mui/icons-material/Shield';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import brokerCompanyApi from '../../services/brokerCompanyApi';
import { slate } from '../../theme/theme';
import FormProvider from '../../components/form/FormProvider';
import TextField from '../../components/form/TextField';
import PhoneField from '../../components/form/PhoneField';
import { normalizePhoneForApi } from '../../utils/phoneUtils';
import SelectField from '../../components/form/SelectField';
import PasswordField from '../../components/form/PasswordField';
import SixDigitCodeInput from '../../components/shared/SixDigitCodeInput';
import authApi from '../../services/authApi';
import { authService } from '../../services/authService';
import { getHomeRoute } from '../../config/roles';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

const STATE_OPTIONS = US_STATES.map(s => ({ value: s, label: s }));

const signupSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.email('A valid email address is required'),
  phone: z.string().optional(),
  userNmls: z.string().optional(),
  companyName: z.string().min(1, 'Company name is required'),
  companyNmls: z.string().min(1, 'Company NMLS # is required'),
  companyWebsite: z.string().optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  passwordConfirmed: z.string().min(1, 'Please confirm your password'),
}).refine(d => d.password === d.passwordConfirmed, {
  message: 'Passwords do not match',
  path: ['passwordConfirmed'],
});

const SELLING_POINTS = [
  { icon: <RocketLaunchIcon />, title: 'Quick Onboarding', desc: 'Submit your application and get approved to start originating' },
  { icon: <CalculateIcon />, title: 'Competitive Pricing', desc: 'Access real-time pricing and lock rates instantly' },
  { icon: <ChecklistIcon />, title: 'Full Pipeline Visibility', desc: 'Track every loan from submission to closing' },
  { icon: <ShieldIcon />, title: 'Dedicated Support', desc: 'Work directly with our team from setup through closing' },
];

const BrokerSignupPage = () => {
  const navigate = useNavigate();
  const [brokerSignup] = brokerCompanyApi.endpoints.brokerSignup.useMutation();
  const [sendSignupVerificationCode] = authApi.endpoints.sendSignupVerificationCode.useMutation();
  const [verifySignupCode] = authApi.endpoints.verifySignupCode.useMutation();

  const [error, setError] = useState('');
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationPassword, setVerificationPassword] = useState('');

  const methods = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: '', lastName: '', emailAddress: '', phone: '', userNmls: '',
      companyName: '', companyNmls: '', companyWebsite: '',
      address1: '', address2: '', city: '', state: '', postalCode: '',
      password: '', passwordConfirmed: '',
    },
  });

  const handleSubmit = async (form) => {
    setError('');
    setErrors([]);
    setLoading(true);
    try {
      await brokerSignup({
        firstName: form.firstName,
        lastName: form.lastName,
        emailAddress: form.emailAddress,
        phone: normalizePhoneForApi(form.phone),
        userNmls: form.userNmls || null,
        password: form.password,
        passwordConfirmed: form.passwordConfirmed,
        companyName: form.companyName,
        companyNmls: form.companyNmls,
        companyWebsite: form.companyWebsite || null,
        companyAddress: {
          address1: form.address1 || null,
          address2: form.address2 || null,
          city: form.city || null,
          state: form.state || null,
          postalCode: form.postalCode || null,
        },
      }).unwrap();
      setVerificationEmail(form.emailAddress);
      setVerificationPassword(form.password);
      setSuccess(true);
    } catch (err) {
      if (err?.data?.errors) {
        setErrors(err.data.errors);
      } else {
        setError(getAxiosErrorMessage(err, 'Something went wrong. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setVerificationError('');
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Please enter the 6-digit code from your email.');
      return;
    }

    setVerificationLoading(true);
    try {
      await verifySignupCode({ emailAddress: verificationEmail, code: verificationCode }).unwrap();
      const result = await authService.login(verificationEmail, verificationPassword);
      navigate(getHomeRoute(result.user.role));
    } catch (err) {
      setVerificationError(getAxiosErrorMessage(err, 'Invalid or expired verification code.'));
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleResendCode = async () => {
    setVerificationError('');
    setResendLoading(true);
    try {
      await sendSignupVerificationCode({ emailAddress: verificationEmail }).unwrap();
    } catch {
      // Always show success posture to avoid account enumeration hints.
    } finally {
      setResendLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Box sx={{ maxWidth: 540, mx: 'auto', px: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box component="img" src="/assets/pp-logo.png" alt="PRO Portal" sx={{ height: 44 }} />
          </Box>
          <Paper elevation={3} sx={{ borderRadius: 3, p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>Verify Your Email</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              We sent a 6-digit code to <strong>{verificationEmail}</strong>. Enter it below to finish setup and access Quick Pricer while your company approval is pending.
            </Typography>

            {verificationError && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{verificationError}</Alert>}

            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <SixDigitCodeInput
                label="Enter Your 6-Digit Verification Code"
                value={verificationCode}
                onChange={setVerificationCode}
                autoFocus
              />
            </Stack>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleVerifyCode}
              disabled={verificationLoading}
              sx={{ mb: 1.5 }}
            >
              {verificationLoading ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> Verifying...</> : 'Verify Code & Continue'}
            </Button>
            <Button
              variant="text"
              color="secondary"
              fullWidth
              onClick={handleResendCode}
              disabled={resendLoading}
              sx={{ mb: 1 }}
            >
              {resendLoading ? <CircularProgress size={14} /> : 'Resend Code'}
            </Button>
            <Button variant="outlined" color="inherit" fullWidth onClick={() => navigate('/login')}>
              Back to Sign In
            </Button>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, bgcolor: 'background.default' }}>

      {/* Left — brand hero */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          width: 420,
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden',
          p: 5,
          color: '#fff',
          background: `linear-gradient(160deg, ${slate[900]} 0%, ${slate[800]} 55%, ${slate[700]} 100%)`,
        }}
      >
        <Box sx={{ position: 'absolute', top: -120, right: -120, width: 340, height: 340, borderRadius: '50%', background: 'radial-gradient(circle, rgba(225,4,4,0.18) 0%, transparent 65%)' }} />
        <Box sx={{ position: 'absolute', bottom: -140, left: -100, width: 380, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(143,163,173,0.14) 0%, transparent 65%)' }} />

        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'inline-flex', bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 2, px: 2, py: 1.25, alignSelf: 'flex-start', mb: 4 }}>
            <Box component="img" src="/assets/pp-logo.png" alt="PRO Portal" sx={{ height: 30 }} />
          </Box>

          <Typography variant="h4" fontWeight={700} sx={{ mb: 1, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
            Partner with United Fidelity Funding
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', mb: 4, lineHeight: 1.7 }}>
            Sign up for PRO Portal to originate, price, lock, and close loans — all in one place.
          </Typography>

          <Stack spacing={2.25}>
            {SELLING_POINTS.map((sp) => (
              <Stack key={sp.title} direction="row" spacing={1.75} alignItems="flex-start">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: '#ff6b6b',
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                >
                  {sp.icon}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>{sp.title}</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{sp.desc}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>

          <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', lineHeight: 1.5, display: 'block', mb: 1 }}>
              Powering Real Originators with the tools they need to grow.
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
              Powered by
            </Typography>
            <Box component="a" href="https://uff.pro" target="_blank" rel="noopener noreferrer">
              <Box component="img" src="/assets/UFF_Logo_Main_2026.png" alt="United Fidelity Funding" sx={{ height: 30, filter: 'brightness(0) invert(1)', opacity: 0.55, '&:hover': { opacity: 0.85 }, transition: 'opacity 0.2s' }} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right — Signup Form */}
      <Box sx={{ flex: 1, p: { xs: 3, sm: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflowY: 'auto' }}>

        {/* Mobile logo */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 3 }}>
          <Box component="img" src="/assets/pp-logo.png" alt="PRO Portal" sx={{ height: 44 }} />
        </Box>

        <Box sx={{ maxWidth: 460, mx: 'auto', width: '100%' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>Broker Signup</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Apply to become a PRO Portal broker partner</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </Alert>
          )}

          <FormProvider methods={methods} onSubmit={handleSubmit} autoComplete="off" sx={{ maxWidth: 440, mx: 'auto', width: '100%' }}>

            {/* Section: Your Information */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5, pb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                <PersonIcon color="secondary" sx={{ fontSize: 16 }} />
                <Typography variant="overline" color="secondary" fontWeight={700} sx={{ letterSpacing: 1 }}>
                  Your Information
                </Typography>
              </Stack>

              <Grid container spacing={1.5}>
                <Grid size={{ xs: 6 }}>
                  <TextField name="firstName" label="First Name" required size="small" disabled={loading} placeholder="First" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField name="lastName" label="Last Name" required size="small" disabled={loading} placeholder="Last" />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField name="emailAddress" label="Email Address" required type="email" size="small" disabled={loading} placeholder="you@company.com" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <PhoneField name="phone" label="Phone" size="small" disabled={loading} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField name="userNmls" label="Individual NMLS #" size="small" disabled={loading} placeholder="1234567" />
                </Grid>
              </Grid>
            </Box>

            {/* Section: Company Information */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5, pb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                <BusinessIcon color="secondary" sx={{ fontSize: 16 }} />
                <Typography variant="overline" color="secondary" fontWeight={700} sx={{ letterSpacing: 1 }}>
                  Company Information
                </Typography>
              </Stack>

              <Grid container spacing={1.5}>
                <Grid size={{ xs: 7 }}>
                  <TextField name="companyName" label="Company Name" required size="small" disabled={loading} placeholder="Acme Mortgage" />
                </Grid>
                <Grid size={{ xs: 5 }}>
                  <TextField name="companyNmls" label="Company NMLS #" required size="small" disabled={loading} placeholder="7654321" />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField name="companyWebsite" label="Company Website" type="url" size="small" disabled={loading} placeholder="https://www.yourcompany.com" />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField name="address1" label="Street Address" size="small" disabled={loading} placeholder="123 Main St" />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField name="address2" label="Address Line 2" size="small" disabled={loading} placeholder="Suite 100" />
                </Grid>
                <Grid size={{ xs: 5 }}>
                  <TextField name="city" label="City" size="small" disabled={loading} placeholder="City" />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <SelectField name="state" label="State" size="small" options={STATE_OPTIONS} disabled={loading} />
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <TextField name="postalCode" label="Zip" size="small" disabled={loading} placeholder="12345" inputProps={{ maxLength: 10 }} />
                </Grid>
              </Grid>
            </Box>

            {/* Section: Password */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5, pb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                <ShieldIcon color="secondary" sx={{ fontSize: 16 }} />
                <Typography variant="overline" color="secondary" fontWeight={700} sx={{ letterSpacing: 1 }}>
                  Account Security
                </Typography>
              </Stack>
              <Stack spacing={1.5}>
                <PasswordField name="password" label="Password" size="small" disabled={loading} autoComplete="new-password" />
                <PasswordField name="passwordConfirmed" label="Confirm Password" size="small" disabled={loading} autoComplete="new-password" />
              </Stack>
            </Box>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>

            <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 2 }}>
              Already have an account?{' '}
              <Link component={RouterLink} to="/login" color="secondary" underline="hover">Sign in</Link>
            </Typography>
          </FormProvider>
        </Box>
      </Box>
    </Box>
  );
};

export default BrokerSignupPage;
